import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import { SystemRoundedCheckbox } from "../../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { type HourRange, TimeRangeListField } from "../../../../components/UIToolkit/Inputs/TimeRangeListField";
import { InfoIconWithTooltip } from "../../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import type { JobShiftHours, ShiftHoursSlot } from "../../../../services/backend.service";
import { FONT_SIZE, INK, NEUTRAL } from "../../../../theme/tokens";
import {
	formatSlots,
	SHIFT_HOURS_SAVE_LOCK_REASON_KEYS,
	SHIFT_HOURS_SOURCE_DESCRIPTIONS,
	SHIFT_HOURS_SOURCE_LABELS,
	SHIFT_HOURS_SOURCE_TONES,
	shiftHoursSaveLockReason,
} from "./recurringDisplay";

const MINUTES_PER_HOUR = 60;

/** "07:30:00" → 7.5. `TimeRangeListField` speaks float hours; the wire speaks ISO times. */
function toFloatHours(time: string): number {
	const [hours, minutes] = time.split(":").map(Number);
	return hours + minutes / MINUTES_PER_HOUR;
}

/** 7.5 → "07:30:00". Rounds to the minute — the pickers cannot produce finer than that. */
function toIsoTime(value: number): string {
	const hours = Math.trunc(value);
	const minutes = Math.round((value - hours) * MINUTES_PER_HOUR);
	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
}

const toRanges = (slots: ShiftHoursSlot[]): HourRange[] => slots.map((slot) => [toFloatHours(slot.start), toFloatHours(slot.end)]);
export const toSlots = (ranges: HourRange[]): ShiftHoursSlot[] => ranges.map(([start, end]) => ({ start: toIsoTime(start), end: toIsoTime(end) }));

/** The row's "save to department" checkbox. Absent for role-mode departments, which keep shift lengths per role. */
export type JobRowSaveControl = { selected: boolean; onToggle: (checked: boolean) => void };

type Props = {
	entry: JobShiftHours;
	edited: boolean;
	slots: ShiftHoursSlot[];
	onChange: (ranges: HourRange[]) => void;
	busy: boolean;
	save: JobRowSaveControl | null;
};

/** One job's hours. Its own component so the visible five and the collapsed remainder render identically. */
export const RecurringShiftHoursJobRow: FC<Props> = ({ entry, edited, slots, onChange, busy, save }) => {
	const { t } = useTranslation();
	// `slots` are the hours on screen, so this is the same rule `isShiftHoursSavable` applies.
	const lockReason = shiftHoursSaveLockReason(entry, slots);
	const savable = lockReason === null;
	const lockedReason = lockReason === null ? undefined : t(SHIFT_HOURS_SAVE_LOCK_REASON_KEYS[lockReason]);
	return (
		<Box
			data-testid={`recurring-shift-hours-job-${entry.jobName}`}
			sx={{
				display: "flex",
				flexDirection: { xs: "column", md: "row" },
				alignItems: { xs: "stretch", md: "center" },
				justifyContent: "space-between",
				gap: 1.5,
				py: 1,
				borderBottom: `1px solid ${NEUTRAL[200]}`,
				"&:last-of-type": { borderBottom: "none" },
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, flex: 1 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
					<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: INK.SECONDARY }}>{entry.jobName}</Typography>
					<MetaChip label={SHIFT_HOURS_SOURCE_LABELS[entry.source]} tone={SHIFT_HOURS_SOURCE_TONES[entry.source]} />
					<InfoIconWithTooltip title={SHIFT_HOURS_SOURCE_DESCRIPTIONS[entry.source]} dataTestId={`recurring-shift-hours-source-info-${entry.jobName}`} />
					{edited && (
						<MetaChip label={t("onboarding_load.recurring_shift_hours_edited")} tone="primary" dataTestId={`recurring-shift-hours-edited-${entry.jobName}`} />
					)}
				</Box>
				{edited && (
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>
						{t("onboarding_load.recurring_shift_hours_was", {
							hours: entry.slots.length > 0 ? formatSlots(entry.slots) : t("onboarding_load.recurring_shift_hours_not_set"),
						})}
					</Typography>
				)}
				{save && (
					<SystemRoundedCheckbox
						// A job a save cannot write shows unticked and locked, with the reason as its tooltip.
						checked={save.selected && savable}
						onChange={save.onToggle}
						label={t("onboarding_load.recurring_shift_hours_save_checkbox")}
						disabled={!savable || busy}
						tooltip={lockedReason}
						data-testid={`recurring-shift-hours-save-${entry.jobName}`}
						sx={{ alignSelf: "flex-start" }}
					/>
				)}
			</Box>
			<TimeRangeListField
				value={toRanges(slots)}
				onChange={onChange}
				disabled={busy}
				dataTestId={`recurring-shift-hours-field-${entry.jobName}`}
				sx={{ flex: 1, minWidth: 0 }}
			/>
		</Box>
	);
};
